/**
 * SavedViewTabs — inline saved-view selector rendered next to the page title.
 *
 * Replaces the old toolbar "Saved views" dropdown. This is the controller: it
 * owns the view list, the rename / share / delete mutations and their modals,
 * and the Cmd/Ctrl+S save shortcut, and renders one of two presentations of
 * the same `SavedViewPresentationProps`:
 *
 * - `SavedViewStrip` (≥ md): a horizontal strip of pills — the "All" pill (or
 *   built-in view pills), one pill per saved view, and a trailing "+".
 * - `SavedViewMenu` (< md): one pill-shaped trigger showing the active view,
 *   opening a menu with every view and the active view's actions. The strip
 *   wraps onto several lines on a phone and its hover-revealed caret is
 *   unreachable on touch.
 */

import { useCallback, useMemo, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

import { useBreakpoint } from '@/hooks/useBreakpoint';
import { ConfirmationDialog } from '@/lib/base/overlays/confirmation-dialog';
import type { ActiveListView, BuiltInViewConfig } from '@/lib/crm/core/types';
import {
  useDeleteSavedFilterView,
  useSavedFilterViewsList,
  useUpdateSavedFilterView,
} from '@/lib/crm/list/hooks/useSavedFilterViews';
import {
  getSaveViewShortcutHint,
  useSaveViewShortcut,
} from '@/lib/crm/list/hooks/useSaveViewShortcut';
import type { SavedFilterView } from '@/types/savedFilterView';

import { buildViewHref } from './buildViewHref';
import { RenameSavedViewModal } from './RenameSavedViewModal';
import { SavedViewMenu } from './SavedViewMenu';
import { SavedViewStrip } from './SavedViewStrip';
import {
  type DefaultViewRef,
  type DirtyViewActions,
  isDefaultRef,
  type SavedViewPresentationProps,
} from './savedViewTypes';

export type { DefaultViewRef } from './savedViewTypes';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface BaseProps {
  orgId: number;
  entityType: string;
  /** Currently active saved view id, or null when the default "All" view is active. */
  activeViewId: string | null;
  /** Called when the user picks a saved view pill. Parent fetches single + applies. */
  onSelectView: (viewId: string) => void;
  /** Called when the user picks the "All" pill. Parent resets to the base view. */
  onSelectAll: () => void;
  /** Called when the user clicks "+". Parent opens the SaveCurrentViewModal. */
  onSaveCurrentView: () => void;
  /**
   * Called after a view is deleted. Parent uses this to fall back to "All"
   * when the deleted view was the active one.
   */
  onViewDeleted?: (viewId: string) => void;
  /**
   * The user's default view for this list, or null when none is set. Absent
   * disables the default-view feature entirely (no markers, no menu items).
   */
  defaultViewRef?: DefaultViewRef | null;
  /** Called when the user sets a pill as their default view. */
  onSetDefaultView?: (ref: DefaultViewRef) => void;
  /** Called when the user clears their currently-set default view. */
  onClearDefaultView?: () => void;
  /**
   * True when the active saved view has unsaved local changes (filters or
   * columns/sort). Saved views are never auto-saved — while dirty, the active
   * pill shows an "edited" dot and its menu offers Save / Discard.
   */
  viewDirty?: boolean;
  /**
   * True when the current user may update the active saved view (owner or
   * org permissions admin). When false, "Save as new view" is offered instead
   * of "Save changes".
   */
  canSaveActiveView?: boolean;
  /** Persist the unsaved changes to the active saved view. */
  onSaveActiveViewChanges?: () => void;
  /**
   * True while that save is in flight. Drives the menu item's pending state
   * and disables Discard, so the action has visible feedback during the
   * round trip.
   */
  isSavingViewChanges?: boolean;
  /** Discard the unsaved changes and restore the view's stored state. */
  onDiscardViewChanges?: () => void;
  /**
   * Whether the current user may create a new saved view at all. Defaults to
   * true; callers pass `false` for read-only viewers so the "New view"
   * control is hidden entirely (not just disabled) — see the residual-
   * affordance pattern in docs/claude for viewer seats.
   */
  canCreateView?: boolean;
}

/** No built-in view tabs — the default "All" pill is rendered instead. */
interface BuiltInViewsAbsent {
  builtInViews?: never;
  activeBuiltInViewId?: never;
  onSelectBuiltInView?: never;
}

/**
 * Built-in view tabs replace the "All" pill. All three props are required
 * together — there's no useful state with only some of them set.
 */
interface BuiltInViewsPresent {
  /** Opt-in built-in view tabs; when present they replace the "All" pill. */
  builtInViews: BuiltInViewConfig[];
  /** Active built-in view id (null when a saved view owns the query). */
  activeBuiltInViewId: string | null;
  /** Called when the user picks a built-in view pill. */
  onSelectBuiltInView: (viewId: string) => void;
}

export type SavedViewTabsProps = BaseProps &
  (BuiltInViewsAbsent | BuiltInViewsPresent);

function hasBuiltInViews(
  props: SavedViewTabsProps
): props is BaseProps & BuiltInViewsPresent {
  return Boolean(props.builtInViews && props.builtInViews.length > 0);
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function SavedViewTabs(props: SavedViewTabsProps) {
  const {
    orgId,
    entityType,
    activeViewId,
    onSelectView,
    onSelectAll,
    onSaveCurrentView,
    onViewDeleted,
    defaultViewRef,
    onSetDefaultView,
    onClearDefaultView,
    viewDirty = false,
    canSaveActiveView = false,
    onSaveActiveViewChanges,
    onDiscardViewChanges,
    isSavingViewChanges = false,
    canCreateView = true,
  } = props;

  const isDesktop = useBreakpoint('md');

  const location = useLocation();
  const [searchParams] = useSearchParams();
  const hrefForView = useCallback(
    (view: ActiveListView | null) =>
      buildViewHref(location.pathname, searchParams, view),
    [location.pathname, searchParams]
  );

  const [viewToRename, setViewToRename] = useState<SavedFilterView | null>(
    null
  );
  const [viewToDelete, setViewToDelete] = useState<SavedFilterView | null>(
    null
  );

  const { data: views } = useSavedFilterViewsList(orgId, entityType);

  const { mutateAsync: updateView, isPending: isUpdating } =
    useUpdateSavedFilterView(orgId, entityType);
  const { mutateAsync: deleteView, isPending: isDeleting } =
    useDeleteSavedFilterView(orgId, entityType);

  // All accessible views (the user's own + team-shared) in one row, ordered
  // oldest-first (left → right). created_at is an ISO-8601 string, so a string
  // compare is chronological. Team-shared views owned by others appear inline
  // by age, with the same list icon; they're apply-only (no management caret,
  // gated on `is_owner` in SavedViewPill).
  const orderedViews = useMemo(() => {
    const visible = (views ?? []).filter(
      v => v.is_owner || v.is_shared_with_org
    );
    return [...visible].sort((a, b) =>
      a.created_at.localeCompare(b.created_at)
    );
  }, [views]);

  const handleToggleShare = useCallback(
    async (view: SavedFilterView) => {
      try {
        await updateView({
          viewId: view.entity_id,
          body: { is_shared_with_org: !view.is_shared_with_org },
        });
      } catch {
        toast.error("We couldn't update sharing. Try again.");
      }
    },
    [updateView]
  );

  const handleStartRename = useCallback((view: SavedFilterView) => {
    setViewToRename(view);
  }, []);

  const handleStartDelete = useCallback((view: SavedFilterView) => {
    setViewToDelete(view);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!viewToDelete) {
      return;
    }
    const deletedId = viewToDelete.entity_id;
    try {
      await deleteView(deletedId);
      setViewToDelete(null);
      // Let the parent fall back to "All" if the deleted view was active.
      onViewDeleted?.(deletedId);
    } catch (err) {
      toast.error("We couldn't delete this view. Try again.");
      throw err;
    }
  }, [deleteView, viewToDelete, onViewDeleted]);

  const handleCancelDelete = useCallback(() => {
    setViewToDelete(null);
  }, []);

  const handleRenameOpenChange = useCallback((open: boolean) => {
    if (!open) {
      setViewToRename(null);
    }
  }, []);

  const isMutationPending = isUpdating || isDeleting;

  const handleToggleDefaultAll = useCallback(() => {
    if (isDefaultRef(defaultViewRef, { kind: 'all' })) {
      onClearDefaultView?.();
    } else {
      onSetDefaultView?.({ kind: 'all' });
    }
  }, [defaultViewRef, onClearDefaultView, onSetDefaultView]);

  const handleToggleDefaultBuiltIn = useCallback(
    (viewId: string) => {
      if (isDefaultRef(defaultViewRef, { kind: 'builtin', id: viewId })) {
        onClearDefaultView?.();
      } else {
        onSetDefaultView?.({ kind: 'builtin', id: viewId });
      }
    },
    [defaultViewRef, onClearDefaultView, onSetDefaultView]
  );

  const handleToggleDefaultSaved = useCallback(
    (view: SavedFilterView) => {
      if (isDefaultRef(defaultViewRef, { kind: 'saved', id: view.entity_id })) {
        onClearDefaultView?.();
      } else {
        onSetDefaultView?.({ kind: 'saved', id: view.entity_id });
      }
    },
    [defaultViewRef, onClearDefaultView, onSetDefaultView]
  );

  // Cmd/Ctrl+S while a saved view is dirty: save it (owner) or save it as a
  // new view (non-owner) — the same action the menu's top item performs.
  const isSavedViewDirty = viewDirty && activeViewId !== null;
  useSaveViewShortcut({
    enabled: isSavedViewDirty,
    isSaving: isSavingViewChanges,
    onSave: canSaveActiveView ? onSaveActiveViewChanges : onSaveCurrentView,
  });

  const dirtyActions = useMemo<DirtyViewActions>(
    () => ({
      canSave: canSaveActiveView,
      isSaving: isSavingViewChanges,
      onSave: onSaveActiveViewChanges,
      onSaveAsNew: onSaveCurrentView,
      onDiscard: onDiscardViewChanges,
      shortcutHint: getSaveViewShortcutHint(),
    }),
    [
      canSaveActiveView,
      isSavingViewChanges,
      onSaveActiveViewChanges,
      onSaveCurrentView,
      onDiscardViewChanges,
    ]
  );

  const presentationProps: SavedViewPresentationProps = {
    activeViewId,
    orderedViews,
    builtInViews: hasBuiltInViews(props) ? props.builtInViews : null,
    activeBuiltInViewId: hasBuiltInViews(props)
      ? props.activeBuiltInViewId
      : null,
    onSelectBuiltInView: hasBuiltInViews(props)
      ? props.onSelectBuiltInView
      : undefined,
    onSelectView,
    onSelectAll,
    onSaveCurrentView,
    defaultViewRef: defaultViewRef ?? null,
    hasDefaultViewFeature: Boolean(onSetDefaultView),
    onToggleDefaultAll: handleToggleDefaultAll,
    onToggleDefaultBuiltIn: handleToggleDefaultBuiltIn,
    onToggleDefaultSaved: handleToggleDefaultSaved,
    onRename: handleStartRename,
    onToggleShare: handleToggleShare,
    onDelete: handleStartDelete,
    isMutationPending,
    viewDirty: isSavedViewDirty,
    dirtyActions,
    canCreateView,
    hrefForView,
  };

  return (
    <>
      {isDesktop ? (
        <SavedViewStrip {...presentationProps} />
      ) : (
        <SavedViewMenu {...presentationProps} />
      )}

      <RenameSavedViewModal
        isOpen={viewToRename !== null}
        onOpenChange={handleRenameOpenChange}
        orgId={orgId}
        entityType={entityType}
        view={viewToRename}
      />
      <ConfirmationDialog
        isOpen={viewToDelete !== null}
        title='Delete view?'
        description={
          viewToDelete
            ? `Delete "${viewToDelete.name}"? Anyone with access will lose it.`
            : ''
        }
        confirmLabel='Delete'
        variant='destructive'
        isLoading={isDeleting}
        onCancel={handleCancelDelete}
        onConfirm={handleConfirmDelete}
      />
    </>
  );
}
